// 📄 services/comercioFacturasService.ts
//
// "Buscar Facturas" (Commerce, 2026-09-07) — wrapper del callable
// `ariba_BuscarFacturas` (ver functions/callable/aribaBuscarFacturas.js),
// mismo criterio que slpProveedoresService.ts: un solo endpoint fijo (SAP
// Business Network Invoice Header Data Extraction → GET /invoices), sin
// CRUD acá, solo ejecutar la búsqueda con los filtros que arma la pantalla.
import { httpsCallable } from 'firebase/functions';
import { functionsClient } from '../firebase';

// Enums documentados por la API — se comparten con la UI para los
// <select> de filtro (ver BuscarFacturas.tsx).
export const DOCUMENT_STATUS_OPCIONES = ["Paid", "Approved", "Canceled", "Failed", "Unknown", "Rejected", "Sent", "In progress", "Pending Approval", "Obsoleted"];
export const ROUTING_STATUS_OPCIONES = ["Sent", "Failed", "On Hold", "Queued", "Acknowledged", "Obsoleted"];
export const DOCUMENT_PURPOSE_OPCIONES = ["standard", "creditMemo", "debitMemo", "lineLevelCreditMemo", "lineLevelDebitMemo"];

export interface FiltrosFacturas {
  buyerANID: string;
  supplierANID: string;
  documentNumber: string;
  documentStatus: string;
  routingStatus: string;
  documentPurpose: string;
  // Formato datetime-local del input ("YYYY-MM-DDTHH:mm") — Ariba exige
  // ambas fechas de un par juntas (o ninguna) y un rango máximo de 31 días
  // (ver validarRangoFechas en el backend, que es la autoridad real).
  startDate: string;
  endDate: string;
  documentStatusUpdatedStartDate: string;
  documentStatusUpdatedEndDate: string;
  orderByField: '' | 'buyerANID' | 'supplierANID';
  orderByDir: 'asc' | 'desc';
  includeInvoiceProfile: boolean;
  top: number;
  skip: number;
}

export const FILTROS_FACTURAS_VACIOS: FiltrosFacturas = {
  buyerANID: '',
  supplierANID: '',
  documentNumber: '',
  documentStatus: '',
  routingStatus: '',
  documentPurpose: '',
  startDate: '',
  endDate: '',
  documentStatusUpdatedStartDate: '',
  documentStatusUpdatedEndDate: '',
  orderByField: '',
  orderByDir: 'desc',
  // Default `false` — a diferencia de los "with*" de Buscar Proveedores,
  // la propia documentación de Ariba dice que el default de este campo es
  // `false` (payload más liviano salvo que el usuario lo pida).
  includeInvoiceProfile: false,
  top: 25,
  skip: 0
};

// La respuesta de Ariba trae los nombres de campo tal cual los documenta
// la API ("documentNumber", "supplierName", "tax.amount" anidado, ...) —
// index signature porque hay direcciones/objetos anidados que la UI
// muestra explícitamente pero que no vale la pena tipar campo por campo.
export interface Factura {
  [campo: string]: any;
}

export interface ResultadoBusquedaFacturas {
  ok: boolean;
  status: number;
  facturas: Factura[];
  count: number;
  limit: number;
  offset: number;
  totalPages: number;
  firstPage: boolean;
  lastPage: boolean;
  durationMs: number;
  error: string | null;
}

export const buscarFacturas = async (
  clienteId: string,
  projectId: string,
  apiId: string,
  filtros: FiltrosFacturas
): Promise<ResultadoBusquedaFacturas> => {
  const fn = httpsCallable<
    { clienteId: string; projectId: string; apiId: string; filtros: FiltrosFacturas },
    ResultadoBusquedaFacturas
  >(functionsClient, 'ariba_BuscarFacturas');
  const result = await fn({ clienteId, projectId, apiId, filtros });
  return result.data;
};
